import { styled } from '@mui/material';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Image from 'next/image';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Caption, Tab } from './typography/legacy/Text';
import { COLORS } from '../lib/colors';

type FooterProps = {
  termsHref?: string;
  privacyPolicyHref?: string;
  consumerHealthDataPolicyHref?: string;
  supportEmail?: string;
};

const StyledTab = styled(Tab)(() => ({
  display: 'inline',
}));

const StyledCaption = styled(Caption)(({ theme }) => ({
  marginTop: '24px',
  color: theme.palette.mode === 'dark' ? 'white' : 'rgba(0, 0, 0, 0.60)',
  textAlign: 'center',
  fontFeatureSettings: '"liga" off, "clig" off',
  fontFamily: theme.typography.body1.fontFamily, // Inter from theme
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '140%', // 19.6px
}));

const FooterContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? COLORS.O4MeDarkGray : COLORS.O4MeLightGray,
  paddingTop: '24px',
  paddingBottom: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingLeft: theme.spacing(3),
  marginTop: 'auto',
  textAlign: 'center',
}));

const LinksContainer = styled(Box)(({}) => ({
  marginRight: 'auto',
  marginLeft: 'auto',
  marginBottom: '24px',
}));

const LinkMode = styled(Link)(({ theme }) => ({
  color: theme.palette.mode === 'dark' ? 'white' : 'rgba(0, 0, 0, 0.60)',
  textAlign: 'center',
  fontFeatureSettings: '"liga" off, "clig" off',
  fontFamily: theme.typography.body1.fontFamily, // Inter from theme
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '140%', // 19.6px
}));

export const Footer: React.FC<FooterProps> = ({
  termsHref = 'https://static.outcomes4me.com/termsofservice/',
  privacyPolicyHref = 'https://static.outcomes4me.com/privacy/',
  consumerHealthDataPolicyHref = 'https://static.outcomes4me.com/health/',
  supportEmail = 'support@outcomes4me.com',
}) => {
  const { t } = useTranslation('footer');

  return (
    <FooterContainer>
      <LinksContainer>
        <StyledTab>
          <LinkMode href="https://outcomes4me.com" target="_blank" underline="hover">
            {t('aboutOutcomes4me', 'About Outcomes4Me')}
          </LinkMode>
        </StyledTab>
        <StyledTab>&nbsp;&nbsp;•&nbsp;&nbsp;</StyledTab>
        <StyledTab>
          <LinkMode href={termsHref} target="_blank" underline="hover">
            {t('termsOfService', 'Terms of Service')}
          </LinkMode>
        </StyledTab>
        <StyledTab>&nbsp;&nbsp;•&nbsp;&nbsp;</StyledTab>
        <StyledTab>
          <LinkMode href={privacyPolicyHref} target="_blank" underline="hover">
            {t('privacyPolicy', 'Privacy Policy')}
          </LinkMode>
        </StyledTab>
        <StyledTab>&nbsp;&nbsp;•&nbsp;&nbsp;</StyledTab>
        <StyledTab>
          <LinkMode href={consumerHealthDataPolicyHref} target="_blank" underline="hover">
            {t('consumerHealthDataPolicy', 'Consumer Health Data Policy')}
          </LinkMode>
        </StyledTab>
        <StyledTab>&nbsp;&nbsp;•&nbsp;&nbsp;</StyledTab>
        <StyledTab>
          <LinkMode href={`mailto:${supportEmail}`} target="_blank" underline="hover">
            {t('giveFeedback', 'Give Feedback')}
          </LinkMode>
        </StyledTab>
      </LinksContainer>
      {/* TODO: replace with ImageWithBasePath common component once piportal is upgraded to Next.js >= 13 */}
      <Image
        src={`${process.env.NEXT_PUBLIC_BASE_PATH ?? ''}/outcomes4me_logo.png`}
        width={160}
        height={24}
        alt={t('outcomes4me', 'Outcomes4Me')}
      />
      <StyledCaption>
        {t('copyrightOutcomes4Me', {
          year: new Date().getFullYear(),
          defaultValue: `© ${new Date().getFullYear()} Outcomes4Me Inc. All rights reserved.`,
        })}
      </StyledCaption>
    </FooterContainer>
  );
};

export default Footer;
